import React from 'react';
import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FlexColumn } from 'ui-components/components/flex';
import { testId } from 'ui-components/services/testid-service';
import { ShipFrom } from './ship-from';
import { Weight } from '../../../../../fulfillment-planner/weight/weight';
import { CSWShippingDefaultsButton } from '../../../../../fulfillment-planner/shipping-default-button';
import { ShippingService } from './shipping-service';
import { RequestedService } from './requested-service';
import { Confirmation } from './confirmation';
import { Insurance } from '../../../../../fulfillment-planner/insurance-option/with-label';
import { useEnableInsurancePromoExperiment } from '../../../../../feature-flags/hooks/use-enable-insurance-promo-experiment';
import { InsurancePromo } from '../../../../../fulfillment-planner/insurance-promo/insurance-promo';
import { messages as fulfillmentPlannerMessages } from '../../../../../fulfillment-planner/messages';
import { ShipFromLabel } from '../../../../../fulfillment-planner/ship-from/label';
import { PackageDropdownContainer } from '../../../../../fulfillment-planner/package/dropdown/container';
import { PackageLabel } from '../../../../../fulfillment-planner/package/label';
import { Package } from './package';
import { PackageContentDescription } from './package-content-description';

import { SmartFillBanner } from '../../../../../orders/order-details/fulfillment-plan/sections/configure-shipment/smartfill-banner';
import { Size } from '../../../../../fulfillment-planner/dimensions/size';
import { FulfillmentPlannerError } from '../../../../../fulfillment-planner/error';
import { RateAndCreateLabel } from '../../../../../fulfillment-planner/rate-and-create-label';
import { DriverTipWithLabel } from '../../../../../fulfillment-planner/local-delivery/driver-tip/with-label';
import { PickupWithLabel } from '../../../../../fulfillment-planner/local-delivery/pickup/with-label';
import { DutiesAndTaxesWithLabel } from '../../../../../fulfillment-planner/duties-and-taxes/with-label';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import { RestrictedShipmentSection } from '../../../../../fulfillment-planner/restricted-shipment-config';
import { FulfillmentPlanService } from '../../../../../services/fulfillment-plan-service';
import { useShouldDisplaySmartFillBanner } from '../../../../../hooks/smartfill/use-smartfill-data';
import { useEnableLtlFreight } from '../../../../../feature-flags/hooks/use-enable-ltl-freight';
import { SIDEBAR } from '../../../../../constants/component-names';
import { ShipmentTypeToggle } from '../../../../../orders/order-details/fulfillment-plan/sections/configure-shipment/shipment-type-toggle/shipment-type-toggle';

import style from './style.module.less';
import { RecommendedShippingService } from './recommended-shipping-service';

const sizeAndWeightClassNames = {
  label: style.label,
  'size-input': style['size-input'],
  'weight-input': style['weight-input'],
  weight: style.weight,
  scale: style.scale,
  container: style['size-and-weight-container'],
};

/**
 * @param {object} props
 * @param {JSX.Element} props.AdditionalContent
 * @param {string} props.fulfillmentPlanId
 * @param {boolean?} props.isTikTokShipping
 * @param {JSX.Element} props.RestrictedBanner
 * @param {boolean?} props.showEnhancedConfig
 * @returns {JSX.Element}
 */
const RestrictedConfiguration = ({
  AdditionalContent = null,
  fulfillmentPlanId,
  isTikTokShipping = false,
  RestrictedBanner,
  showEnhancedConfig = false,
}) => {
  if (!showEnhancedConfig) {
    return (
      <div className={style['drawer-fulfillment-configuration']}>
        <ShipFrom fulfillmentPlanId={fulfillmentPlanId} hideFulfillmentProviders hideRateBrowserButton />
        {RestrictedBanner && (
          <div className={style['full-width-content']}>
            <RestrictedBanner />
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={style['drawer-fulfillment-configuration']}>
      {RestrictedBanner && (
        <div className={style['full-width-content']}>
          <RestrictedBanner />
        </div>
      )}
      {isTikTokShipping ? (
        <>
          <div className={style.label}>
            <ShipFromLabel />
          </div>
          <div className={style['full-width-content']}>
            <FormattedMessage {...fulfillmentPlannerMessages.tikTokShipFromText} />
          </div>
        </>
      ) : (
        <ShipFrom fulfillmentPlanId={fulfillmentPlanId} hideFulfillmentProviders hideRateBrowserButton />
      )}
      <Weight classNames={sizeAndWeightClassNames} showScale />
      {isTikTokShipping ? (
        <>
          <div className={style.label}>
            <PackageLabel />
          </div>
          <PackageDropdownContainer isDisabled />
        </>
      ) : (
        <Package />
      )}
      <Size classNames={sizeAndWeightClassNames} fulfillmentPlanId={fulfillmentPlanId} />
      {AdditionalContent && (
        <div className={style['full-width-content']}>
          <AdditionalContent />
        </div>
      )}
    </div>
  );
};

export const DrawerShipmentConfiguration = ({ fulfillmentPlanId }) => {
  const { uiRestrictions, hasSmartFillDataBeenChanged, isSmartFillEnabled, parentComponentName } = useFulfillmentPlannerContext();
  const hideConfigureShipment = FulfillmentPlanService.hasLabelConfigurationRestriction(uiRestrictions);
  const isInsurancePromoExperimentEnabled = useEnableInsurancePromoExperiment();
  const isLtlFreightEnabled = useEnableLtlFreight();

  const hasAllSmartFillDataChanged = Object.values(hasSmartFillDataBeenChanged).every((val) => val);
  const shouldDisplaySmartFillBanner = useShouldDisplaySmartFillBanner(fulfillmentPlanId);
  const showSmartFillBanner = isSmartFillEnabled && shouldDisplaySmartFillBanner && !hasAllSmartFillDataChanged;

  return (
    <div {...testId('csw')}>
      {hideConfigureShipment ? (
        <RestrictedShipmentSection Config={RestrictedConfiguration} fulfillmentPlanId={fulfillmentPlanId} />
      ) : (
        <>
          {showSmartFillBanner && (
            <FlexColumn className={style['drawer-fulfillment-smartfill-banner']}>
              <SmartFillBanner />
            </FlexColumn>
          )}
          {isLtlFreightEnabled && parentComponentName === SIDEBAR && (
            <ShipmentTypeToggle className={style['shipment-toggle-padding']} fulfillmentPlanId={fulfillmentPlanId} />
          )}
          <div className={style['drawer-fulfillment-configuration']}>
            <RequestedService fulfillmentPlanId={fulfillmentPlanId} />
            <ShipFrom fulfillmentPlanId={fulfillmentPlanId} />
            <Weight classNames={sizeAndWeightClassNames} fulfillmentPlanId={fulfillmentPlanId} showScale />
            <ShippingService fulfillmentPlanId={fulfillmentPlanId} />
            <RecommendedShippingService />
            <PickupWithLabel fulfillmentPlanId={fulfillmentPlanId} />
            <Package />
            <PackageContentDescription />
            <Size classNames={sizeAndWeightClassNames} fulfillmentPlanId={fulfillmentPlanId} />
            <Confirmation fulfillmentPlanId={fulfillmentPlanId} />
            {isInsurancePromoExperimentEnabled ? (
              <InsurancePromo className={style['insurance-promo-layout']}>
                <div className={classnames(style['drawer-fulfillment-configuration'], style['insurance-promo-overrides'])}>
                  <Insurance fulfillmentPlanId={fulfillmentPlanId} isPartOfInsurancePromoExperiment />
                </div>
              </InsurancePromo>
            ) : (
              <Insurance fulfillmentPlanId={fulfillmentPlanId} />
            )}
            <DutiesAndTaxesWithLabel fulfillmentPlanId={fulfillmentPlanId} />
            <DriverTipWithLabel fulfillmentPlanId={fulfillmentPlanId} />
            <FulfillmentPlannerError className={style['error-container']} />
          </div>
          <CSWShippingDefaultsButton fulfillmentPlanId={fulfillmentPlanId} />
        </>
      )}
      <RateAndCreateLabel fulfillmentPlanId={fulfillmentPlanId} isQuickshipCompatible />
    </div>
  );
};

DrawerShipmentConfiguration.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
};
